import React from "react";
import { useNavigate } from "react-router-dom";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import { useCreateTestAgentMutation } from "../../graphql/generated";
import { MlsContext } from "../../mls/context/mls";
import { useTitle } from "../../common/utils/title";

const AgentNew: React.FC = () => {
  useTitle("New", "Agents");
  const navigate = useNavigate();

  const { mlses } = React.useContext(MlsContext);
  const { notifier } = React.useContext(NotificationContext);

  const [mlsState, setMlsState] = React.useState("");
  const [firstNameState, setFirstNameState] = React.useState("");
  const [lastNameState, setLastNameState] = React.useState("");
  const [mlsIdState, setMlsIdState] = React.useState("");
  const [emailState, setEmailState] = React.useState("");

  const [{ error, data, fetching }, mutation] = useCreateTestAgentMutation();

  const submitForm = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      mutation({
        details: {
          firstName: firstNameState,
          lastName: lastNameState,
          memberId: mlsIdState,
          mls: mlsState,
          email: emailState,
        },
      });
    },
    [emailState, firstNameState, lastNameState, mlsIdState, mlsState, mutation]
  );

  React.useEffect(() => {
    if (!error && data && !fetching) {
      navigate("/agents");
    } else if (error) {
      notifier.error(error);
    }
  }, [data, error, fetching, navigate]);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              New Test Agent
            </h2>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:mx-4 sm:rounded-md">
        <div className="p-4">
          <form
            className="space-y-8 divide-y divide-gray-200"
            onSubmit={submitForm}
          >
            <div className="space-y-8 divide-y divide-gray-200 sm:space-y-5">
              <div>
                <div className="space-y-6 sm:space-y-5">
                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
                    <label
                      htmlFor="mls"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      MLS
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <select
                        required
                        id="mls"
                        name="mls"
                        value={mlsState}
                        onChange={(e) => setMlsState(e.target.value)}
                        className="max-w-lg block focus:ring-indigo-500 focus:border-indigo-500 w-full shadow-sm sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      >
                        <option value={""}>Please Select</option>
                        {mlses.map((mls) => (
                          <option value={mls.id}>
                            {mls.name} - {mls.shortName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      First Name
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="text"
                        name="first-name"
                        id="first-name"
                        value={firstNameState}
                        onChange={(e) => setFirstNameState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="last-name"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      Last Name
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="text"
                        name="last-name"
                        id="last-name"
                        value={lastNameState}
                        onChange={(e) => setLastNameState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="member-id"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      MLS Member ID
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="text"
                        name="member-id"
                        id="member-id"
                        value={mlsIdState}
                        onChange={(e) => setMlsIdState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      Email
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="email"
                        name="email"
                        id="email"
                        value={emailState}
                        onChange={(e) => setEmailState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  //   disabled={fetching}
                  type="submit"
                  className={classNames(
                    // eslint-disable-next-line no-constant-condition
                    true
                      ? "text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      : "text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500",
                    "ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md"
                  )}
                >
                  Create
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AgentNew;
